import {
  BroadcastFn,
  SchemaInstructionsHelper,
} from '@backstage-components/base';
import {
  Duration,
  differenceInDays,
  intervalToDuration,
  isValid,
  parseISO,
} from 'date-fns';
import {Observable, interval, map, of, takeWhile} from 'rxjs';
import {assign, createMachine} from 'xstate';
import {ComponentDefinition} from '../CountdownDefinition';

const HOUR_AS_MS = 60 /*min*/ * 60 /*sec*/ * 1000; /*ms*/
const TWO_HOURS_AS_MS = 2 * HOUR_AS_MS;

const countdownStatusMachine = createMachine<Context, Action, Typestate>(
  {
    id: 'countdown',
    initial: 'INITIAL',
    predictableActionArguments: true,
    states: {
      INITIAL: {
        on: {
          'Countdown:changeTimezone': {
            actions: ['updateTimezone'],
          },
          'Countdown:setTargetTime': {
            actions: ['updateEndTime'],
          },
          'Countdown:ready': {
            actions: ['onReady'],
            target: 'COUNTING',
          },
          'Countdown:complete': 'COMPLETE',
        },
      },
      COUNTING: {
        invoke: {
          src: (context) => countdown(context),
          onDone: 'COMPLETE',
        },
        on: {
          'Countdown:tick': {
            actions: ['onTick'],
          },
          'Countdown:changeTimezone': {
            actions: ['updateTimezone'],
          },
          'Countdown:ended': 'DONE',
          'Countdown:setTargetTime': {
            actions: ['updateEndTime'],
            target: 'COUNTING',
          },
        },
      },
      COMPLETE: {
        entry: ['onComplete'],
        on: {
          'Countdown:changeTimezone': {
            actions: ['updateTimezone'],
          },
          'Countdown:ended': 'DONE',
          'Countdown:setTargetTime': {
            actions: ['updateEndTime'],
            target: 'COUNTING',
          },
        },
      },
      DONE: {
        entry: ['onEvent'],
        type: 'final',
      },
    },
  },
  {
    actions: {
      /**
       * Be careful where this is used, it does not filter out any instruction
       * types as long as they are defined in the `instructions` schema, which
       * may make it possible to trigger a broadcast loop (e.g. with
       * `setTargetTime` or any other instruction the component is receiving
       * from the broker).
       */
      onEvent: (context, event) => context.broadcast(event),
      onComplete: (context) => {
        context.broadcast({
          type: 'Countdown:on-complete',
          meta: {currentTime: new Date().toISOString()},
        });
      },
      onReady: (context) => {
        context.broadcast({type: 'Countdown:on-ready', meta: {}});
      },
      onTick: assign({
        remaining: (context, event) => {
          if (event.type === 'Countdown:tick') {
            return intervalToCountdown({
              start: parseISO(event.meta.currentTime),
              end: context.endTime,
            });
          } else {
            return context.remaining;
          }
        },
      }),
      updateEndTime: assign({
        endTime: (context, event) => {
          if (event.type !== 'Countdown:setTargetTime') {
            return context.endTime;
          }
          const nextEnd = parseISO(event.meta.targetTime);
          if (isValid(nextEnd)) {
            return nextEnd;
          } else {
            return context.endTime;
          }
        },
      }),
      updateTimezone: assign({
        timezoneOffset: (context, event) =>
          event.type === 'Countdown:changeTimezone'
            ? event.meta.timezoneOffset
            : context.timezoneOffset,
      }),
    },
  }
);

/**
 * Creates a new Countdown state machine with the given options as context.
 * @param options to use when creating the machine, used as the initial context.
 * @returns a new Countdown state machine instance.
 */
export function createCountdownStatusMachine(
  options: Partial<Context>
): typeof countdownStatusMachine {
  return countdownStatusMachine.withContext(fromOptions(options));
}

interface Context {
  /**
   * Used to broadcast into local broker.
   */
  broadcast: BroadcastFn<(typeof ComponentDefinition)['instructions']>;
  /**
   * Indicates the time when the countdown will be completed.
   * @default Date.now() + 2 HOURS
   */
  endTime: Date;
  /**
   * Object representing the remaining duration between the time from the last
   * tick and the `endTime`.
   */
  remaining: Duration;
  /**
   * How often the countdown should tick (in ms).
   * @default 1000
   */
  tickDuration: number;
  /**
   * The offset of the display timezone from UTC.
   * @default new Date().getTimezoneOffset()
   */
  timezoneOffset: number;
}

type Action = SchemaInstructionsHelper<typeof ComponentDefinition>;

type Typestate =
  | {value: 'INITIAL'; context: Context}
  | {value: 'COUNTING'; context: Context}
  | {value: 'COMPLETE'; context: Context}
  | {value: 'DONE'; context: Context};

/**
 * Create an `Observable` of `Action`, it produces "tick" actions until the
 * `endTime` is past at which point it will produce a "complete" action.
 * @param context holding the `endTime`.
 * @private exported for tests
 */
export function countdown(
  context: Pick<Context, 'endTime' | 'tickDuration'>
): Observable<Select<Action, 'Countdown:tick'>> {
  if (context.endTime.getTime() - Date.now() <= 0) {
    return of();
  }
  const CountdownComplete = Symbol('CountdownComplete');
  const computeTimeRemaining = (): number =>
    context.endTime.getTime() - Date.now();
  return interval(context.tickDuration).pipe(
    map((): Select<Action, 'Countdown:tick'> | typeof CountdownComplete => {
      const remaining = computeTimeRemaining();
      if (remaining <= 0) {
        return CountdownComplete;
      } else {
        return {
          type: 'Countdown:tick',
          meta: {currentTime: new Date().toISOString()},
        };
      }
    }),
    takeWhile(
      (value): value is Select<Action, 'Countdown:tick'> =>
        value !== CountdownComplete
    )
  );
}

/**
 * Our UI's largest time unit is days, so return only days, hours, minutes, and
 * seconds. For countdowns that have already completed, send back 0 for all.
 */
export function intervalToCountdown({start, end}: Interval): Duration {
  if (end <= start) {
    return {days: 0, hours: 0, minutes: 0, seconds: 0};
  }
  const remaining = intervalToDuration({start, end});
  return {
    // This signature is correct. `end` first.
    days: differenceInDays(end, start),
    hours: remaining.hours,
    minutes: remaining.minutes,
    seconds: remaining.seconds,
  };
}

function fromOptions(options: Partial<Context>): Context {
  const noop: Context['broadcast'] = () => {
    /* no-op */
  };
  const now = Date.now();
  const endTime = options.endTime ?? new Date(now + TWO_HOURS_AS_MS);
  return {
    broadcast: options.broadcast ?? noop,
    endTime,
    remaining: intervalToCountdown({start: now, end: endTime}),
    tickDuration: options.tickDuration ?? 1000,
    timezoneOffset:
      typeof options.timezoneOffset === 'undefined'
        ? new Date(now).getTimezoneOffset()
        : options.timezoneOffset,
  };
}

/**
 * Select an action or instruction based on the `type` key.
 */
type Select<T, Type extends Action['type']> = T extends {type: Type}
  ? T
  : never;
